import { useMemo } from 'react';
import { pruneSelection } from '../utils/docketFilter';
import { useDocketFilterOptions } from './useDocketFilterOptions';
import { useSession } from './useSession';

export interface DocketFilter {
  selectedDeviceIds?: string[];
  selectedSectionIds?: string[];
}

// Gives the device and section ids this screen should show dockets for, from the saved setting.
// Saved ids that no longer exist are dropped. An absent key means no filter on that list.
export function useDocketFilter(): DocketFilter {
  const { session } = useSession();
  const savedDeviceIds = session?.kitchenDisplay?.devices;
  const savedSectionIds = session?.kitchenDisplay?.sections;
  const hasSavedSelection =
    !!savedDeviceIds?.length || !!savedSectionIds?.length;
  const { devices, sections } = useDocketFilterOptions({
    skip: !hasSavedSelection,
  });

  const deviceIds = useMemo(() => devices?.map(d => d.id), [devices]);
  const sectionIds = useMemo(() => sections?.map(s => s.id), [sections]);

  const selectedDeviceIds = useMemo(
    () => pruneSelection(savedDeviceIds, deviceIds),
    [savedDeviceIds, deviceIds],
  );
  const selectedSectionIds = useMemo(
    () => pruneSelection(savedSectionIds, sectionIds),
    [savedSectionIds, sectionIds],
  );

  return useMemo(
    () => ({
      ...(selectedDeviceIds && { selectedDeviceIds }),
      ...(selectedSectionIds && { selectedSectionIds }),
    }),
    [selectedDeviceIds, selectedSectionIds],
  );
}
